import {
  Component,
  ElementRef,
  NgZone,
  OnInit,
  Input,
  ViewChild,
  OnChanges, SimpleChanges
} from '@angular/core';
import {
  Firestore,
  collection,
  query,
  doc,
  updateDoc,
  where,
  onSnapshot,
  collectionGroup
} from '@angular/fire/firestore';
import { Subscription } from 'rxjs';
import { SearchResult, Plan, AdditionalInfo } from '../../interfaces/searchResult.interface';
import { AuthService } from '../../services/auth.service';
import { NgbModal, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';
import { Timestamp } from 'firebase/firestore';
import { ToastrService } from 'ngx-toastr';
import { PdfComponent } from '../pdf/pdf.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgxDocViewerModule } from 'ngx-doc-viewer';

interface UploadedFile {
  url: string;
  name: string;
  type: string;
}

interface Problem {
  name: string;
  tag: string[];
}

@Component({
  selector: 'app-lichSuDichVu',
  templateUrl: './lichSuDichVu.component.html',
  styleUrls: ['./lichSuDichVu.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgbModalModule,
    NgxDocViewerModule,
    PdfComponent
  ]
})
export class LichSuDichVu implements OnInit, OnChanges {
  @ViewChild('pdfComp') pdfComponent!: PdfComponent;
  @ViewChild('additionalInfoModal') additionalInfoModal: any;
  @Input() userEmail!: string;
  orderList: SearchResult[] = [];
  selectedDocument: SearchResult | null = null;

  loadingFileList: boolean = false;
  loadingFileSelectedOther: boolean;
  currentViewedFile: UploadedFile | null = null;

  showPlanDocList: boolean = true;

  private unsubscribeSnapshot?: () => void;

  additionalInfo: AdditionalInfo = {
    vitalSigns: {},
    conclusion: {}
  };
  savingAdditionalInfo: boolean = false;

  constructor(
    public ngZone: NgZone,
    public firestore: Firestore,
    public docModal: NgbModal,
    public authService: AuthService,
    private toastr: ToastrService,
  ) { }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userEmail'] && this.userEmail) {
      this.loadPlans();
    }
  }

  loadPlans(): void {
    const plansQuery = query(
      collectionGroup(this.firestore, 'plans'),
      where('clientEmail', '==', this.userEmail)
    );

    this.unsubscribeSnapshot = onSnapshot(plansQuery, (plansSnapshot) => {
      const plans = plansSnapshot.docs.map((doc) => ({
        documentId: doc.id,
        docPath: doc.ref.path,
        createdAt: doc.data().createdAt,
        providerEmail: doc.data().providerEmail,
        guide: doc.data().guide || [],
        medications: doc.data().medications || [],
        status: doc.data().status || [],
        files: doc.data().files || [],
        followUpDate: doc.data().followUpDate || null, // Thêm dòng này
      })) as SearchResult[];

      plans.sort((a, b) => {
        const dateA = a.createdAt instanceof Timestamp ? a.createdAt.toDate() : a.createdAt;
        const dateB = b.createdAt instanceof Timestamp ? b.createdAt.toDate() : b.createdAt;
        return dateB.getTime() - dateA.getTime();
      });

      this.orderList = plans;
    });
  }

  selectDocument(document: SearchResult) {
    this.currentViewedFile = null;
    this.selectedDocument = document;
  }

  viewUploadedFile(file: UploadedFile) {
    this.currentViewedFile = {
      url: file.url, // Sử dụng URL đã có trong đối tượng UploadedFile
      type: file.type.startsWith('image/') ? 'image' : 'other',
      name: file.name,
    };
  }

  contentLoaded() {
    this.loadingFileSelectedOther = false;
  }

  async updatePlan() {
    if (this.selectedDocument) {
      const documentId = this.selectedDocument.documentId; // Lấy documentId từ selectedDocument
      const updatedStatuses = [...(this.selectedDocument.status || [])]; // Sao chép mảng hiện tại
      if (!updatedStatuses.includes('6')) {
        updatedStatuses.push('6'); // Thêm giá trị '6' nếu chưa có
      }

      const updatedData = {
        status: updatedStatuses,
        updatedAt: Timestamp.now(),
      };

      try {
        if (this.selectedDocument && this.selectedDocument.docPath) {
          const documentRef = doc(this.firestore, this.selectedDocument.docPath);
          await updateDoc(documentRef, updatedData);
        }
        this.toastr.success('Chúng tôi sẽ liên hệ lại!', 'Đặt mua thuốc thành công!');
      } catch (error) {
        console.error('Error updating document:', error);
      }
    }
  }

  async complain(isComplain: boolean) {
    if (this.selectedDocument && this.selectedDocument.docPath) {
      let updatedStatuses = [...(this.selectedDocument.status || [])];

      if (isComplain) {
        // Thêm '7' nếu chưa có
        if (!updatedStatuses.includes('7')) {
          updatedStatuses.push('7');
        }
      } else {
        // Xóa '7' nếu đang có
        updatedStatuses = updatedStatuses.filter(status => status !== '7');
      }

      const updatedData = {
        status: updatedStatuses,
        updatedAt: Timestamp.now(),
      };

      try {
        const documentRef = doc(this.firestore, this.selectedDocument.docPath);
        await updateDoc(documentRef, updatedData);
        if (isComplain) {
          this.toastr.success('Chúng tôi sẽ liên hệ lại!', 'Dịch vụ sẽ được xem xét!');
        } else {
          this.toastr.success('Đã hủy khiếu nại!', 'Xin cảm ơn bạn!');
        }
      } catch (error) {
        console.error('Error updating document:', error);
      }
    }
  }

  private adjustUI() {
    const buttons = document.querySelectorAll('button');

    buttons.forEach((button) => {
      (button as HTMLElement).style.width = '100%';
      (button as HTMLElement).style.bottom = '10px'; // Điều chỉnh khoảng cách từ đáy
    });
  }

  ngOnDestroy(): void {
    if (this.unsubscribeSnapshot) {
      this.unsubscribeSnapshot();
      this.unsubscribeSnapshot = undefined;
    }
  }

  downloadPDF() {
    if (!this.pdfComponent) {
      console.error('pdfComponent is undefined!');
      return;
    }
    this.pdfComponent.createAndDownloadPDF();
  }

  openAdditionalInfoModal(): void {
    // Khởi tạo dữ liệu từ selectedDocument nếu có
    if (this.selectedDocument?.additionalInfo) {
      this.additionalInfo = {
        ...this.selectedDocument.additionalInfo,
        vitalSigns: this.selectedDocument.additionalInfo.vitalSigns || {},
        conclusion: this.selectedDocument.additionalInfo.conclusion || {}
      };
    } else {
      // Reset form với các trường cơ bản
      this.additionalInfo = {
        // Thông tin cá nhân
        occupation: '',
        emergencyContact: '',
        emergencyPhone: '',
        insuranceNumber: '',
        idNumber: '',

        // Tiền sử bệnh
        familyHistory: '',
        allergies: '',
        currentMedications: '',
        surgicalHistory: '',
        hasPastInjuries: false,
        hasDiabetes: false,
        hasHeartDisease: false,
        hasLungDisease: false,
        isPregnantOrNursing: false,

        // Triệu chứng
        chiefComplaint: '',
        symptomDuration: '',
        symptomSeverity: '',
        associatedSymptoms: '',

        // Thông tin cơ bản
        vitalSigns: {
          weight: '',
          height: '',
          bmi: '',
          bloodPressure: ''
        },

        conclusion: {}
      };
    }

    this.docModal.open(this.additionalInfoModal, {
      size: 'xl',
      centered: true,
      backdrop: 'static',
      scrollable: true
    });
  }

  async saveAdditionalInfo(modal: any): Promise<void> {
    // Validate trước khi lưu
    if (!this.validatePatientInfo()) {
      return;
    }

    this.savingAdditionalInfo = true;

    try {
      // Tính BMI nếu có chiều cao và cân nặng
      if (this.additionalInfo.vitalSigns?.height && this.additionalInfo.vitalSigns?.weight) {
        const height = parseFloat(this.additionalInfo.vitalSigns.height) / 100; // chuyển cm sang m
        const weight = parseFloat(this.additionalInfo.vitalSigns.weight);
        if (height > 0 && weight > 0) {
          const bmi = (weight / (height * height)).toFixed(1);
          this.additionalInfo.vitalSigns.bmi = bmi;
        }
      }

      // Lọc chỉ những thông tin người bệnh có thể khai báo
      const patientInfo = {
        // Thông tin cá nhân
        occupation: this.additionalInfo.occupation || '',
        emergencyContact: this.additionalInfo.emergencyContact || '',
        emergencyPhone: this.additionalInfo.emergencyPhone || '',
        insuranceNumber: this.additionalInfo.insuranceNumber || '',
        idNumber: this.additionalInfo.idNumber || '',

        // Tiền sử bệnh
        familyHistory: this.additionalInfo.familyHistory || '',
        allergies: this.additionalInfo.allergies || '',
        currentMedications: this.additionalInfo.currentMedications || '',
        surgicalHistory: this.additionalInfo.surgicalHistory || '',
        hasPastInjuries: this.additionalInfo.hasPastInjuries || false,
        hasDiabetes: this.additionalInfo.hasDiabetes || false,
        hasHeartDisease: this.additionalInfo.hasHeartDisease || false,
        hasLungDisease: this.additionalInfo.hasLungDisease || false,
        isPregnantOrNursing: this.additionalInfo.isPregnantOrNursing || false,

        // Triệu chứng
        chiefComplaint: this.additionalInfo.chiefComplaint || '',
        symptomDuration: this.additionalInfo.symptomDuration || '',
        symptomSeverity: this.additionalInfo.symptomSeverity || '',
        associatedSymptoms: this.additionalInfo.associatedSymptoms || '',

        // Thông tin sức khỏe cơ bản
        vitalSigns: {
          weight: this.additionalInfo.vitalSigns?.weight || '',
          height: this.additionalInfo.vitalSigns?.height || '',
          bmi: this.additionalInfo.vitalSigns?.bmi || '',
          bloodPressure: this.additionalInfo.vitalSigns?.bloodPressure || ''
        },

        // Thời gian cập nhật
        patientInfoUpdatedAt: new Date()
      };

      // Cập nhật document với thông tin bổ sung - sử dụng documentId từ SearchResult interface
      if (this.selectedDocument?.documentId) {
        const docRef = doc(this.firestore, 'orders', this.selectedDocument.documentId);

        // Merge với thông tin hiện có để không ghi đè thông tin bác sĩ
        await updateDoc(docRef, {
          'additionalInfo.occupation': patientInfo.occupation,
          'additionalInfo.emergencyContact': patientInfo.emergencyContact,
          'additionalInfo.emergencyPhone': patientInfo.emergencyPhone,
          'additionalInfo.insuranceNumber': patientInfo.insuranceNumber,
          'additionalInfo.idNumber': patientInfo.idNumber,
          'additionalInfo.familyHistory': patientInfo.familyHistory,
          'additionalInfo.allergies': patientInfo.allergies,
          'additionalInfo.currentMedications': patientInfo.currentMedications,
          'additionalInfo.surgicalHistory': patientInfo.surgicalHistory,
          'additionalInfo.hasPastInjuries': patientInfo.hasPastInjuries,
          'additionalInfo.hasDiabetes': patientInfo.hasDiabetes,
          'additionalInfo.hasHeartDisease': patientInfo.hasHeartDisease,
          'additionalInfo.hasLungDisease': patientInfo.hasLungDisease,
          'additionalInfo.isPregnantOrNursing': patientInfo.isPregnantOrNursing,
          'additionalInfo.chiefComplaint': patientInfo.chiefComplaint,
          'additionalInfo.symptomDuration': patientInfo.symptomDuration,
          'additionalInfo.symptomSeverity': patientInfo.symptomSeverity,
          'additionalInfo.associatedSymptoms': patientInfo.associatedSymptoms,
          'additionalInfo.vitalSigns.weight': patientInfo.vitalSigns.weight,
          'additionalInfo.vitalSigns.height': patientInfo.vitalSigns.height,
          'additionalInfo.vitalSigns.bmi': patientInfo.vitalSigns.bmi,
          'additionalInfo.vitalSigns.bloodPressure': patientInfo.vitalSigns.bloodPressure,
          'additionalInfo.patientInfoUpdatedAt': patientInfo.patientInfoUpdatedAt,
          updatedAt: new Date()
        });

        // Cập nhật selectedDocument local
        if (!this.selectedDocument.additionalInfo) {
          this.selectedDocument.additionalInfo = {};
        }

        // Merge thông tin mới với thông tin cũ
        Object.assign(this.selectedDocument.additionalInfo, patientInfo);

        this.toastr.success('Đã cập nhật thông tin cá nhân thành công!', 'Thành công');
        modal.close('saved');
      } else {
        throw new Error('Không tìm thấy documentId trong selectedDocument');
      }
    } catch (error) {
      console.error('Lỗi khi lưu thông tin bổ sung:', error);
      this.toastr.error('Có lỗi xảy ra khi lưu thông tin', 'Lỗi');
    } finally {
      this.savingAdditionalInfo = false;
    }
  }

  calculateBMI(): void {
    if (this.additionalInfo.vitalSigns?.height && this.additionalInfo.vitalSigns?.weight) {
      const height = parseFloat(this.additionalInfo.vitalSigns.height) / 100; // chuyển cm sang m
      const weight = parseFloat(this.additionalInfo.vitalSigns.weight);

      if (height > 0 && weight > 0) {
        const bmi = (weight / (height * height)).toFixed(1);

        // Thêm phân loại BMI
        let bmiCategory = '';
        const bmiValue = parseFloat(bmi);
        if (bmiValue < 18.5) {
          bmiCategory = ' (Thiếu cân)';
        } else if (bmiValue < 25) {
          bmiCategory = ' (Bình thường)';
        } else if (bmiValue < 30) {
          bmiCategory = ' (Thừa cân)';
        } else {
          bmiCategory = ' (Béo phì)';
        }

        this.additionalInfo.vitalSigns.bmi = bmi + bmiCategory;
      }
    }
  }

  private validatePatientInfo(): boolean {
    // Kiểm tra số điện thoại khẩn cấp
    if (this.additionalInfo.emergencyPhone && !/^[0-9+\-\s()]+$/.test(this.additionalInfo.emergencyPhone)) {
      this.toastr.warning('Số điện thoại khẩn cấp không hợp lệ', 'Cảnh báo');
      return false;
    }

    // Kiểm tra CMND/CCCD
    if (this.additionalInfo.idNumber && !/^[0-9]{9,12}$/.test(this.additionalInfo.idNumber)) {
      this.toastr.warning('Số CMND/CCCD phải từ 9-12 chữ số', 'Cảnh báo');
      return false;
    }

    // Kiểm tra cân nặng và chiều cao
    if (this.additionalInfo.vitalSigns?.weight) {
      const weight = parseFloat(this.additionalInfo.vitalSigns.weight);
      if (weight <= 0 || weight > 300) {
        this.toastr.warning('Cân nặng không hợp lệ (1-300kg)', 'Cảnh báo');
        return false;
      }
    }

    if (this.additionalInfo.vitalSigns?.height) {
      const height = parseFloat(this.additionalInfo.vitalSigns.height);
      if (height <= 0 || height > 250) {
        this.toastr.warning('Chiều cao không hợp lệ (1-250cm)', 'Cảnh báo');
        return false;
      }
    }

    return true;
  }

  isFollowUpTomorrow(followUpDate: any): boolean {
    if (!followUpDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = typeof followUpDate.toDate === 'function'
      ? followUpDate.toDate()
      : new Date(followUpDate);
    date.setHours(0, 0, 0, 0);
    const diff = (date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);
    return diff === 1;
  }
}
